import { z } from 'zod';
import { CadObjectSummarySchema } from './cad-object.model';
import { ConsumableLink, type ConsumableSelection } from './consumable.model';
import { MaterialSheetSizeItem } from './material.model';
import { QuoteItemEquipment } from './quote-item-equipment.model';
import { DFMCheckSchema } from './dfm-check.model';
import {
  PricingInformationEntry,
  PricingParameters,
  QuantityMode,
  QuantityType,
} from './quote-item-data.model';

const DrawingSummary = z.object({
  id: z.number(),
  filename: z.string(),
});

export const QuoteRevisionItem = z.object({
  id: z.number(),
  created_date: z.iso.datetime(),
  persistent_id: z.string(),
  parent_persistent_id: z.string().nullable(),
  sibling_order: z.number(),
  full_index: z.array(z.number()),
  name: z.string(),
  quantity: z.coerce.number(),
  quantity_mode: z.enum(QuantityMode),
  quantity_type: z.enum(QuantityType),
  unit_cost: z.string(),
  unit_markup: z.string(),
  unit_price: z.string(),
  unit_tax: z.string().nullable(),
  unit_capex: z.string(),
  fixed_costs: z.string().nullable(),
  fixed_costs_markup: z.string().nullable(),
  fixed_price: z.string().nullable(),
  fixed_capex: z.string().nullable(),
  unit_consumables: z.string(),
  fixed_consumables: z.string().nullable(),
  subtotal_cost: z.string(),
  subtotal_markup: z.string(),
  subtotal_capex: z.string(),
  subtotal_consumables: z.string(),
  subtotal_tax: z.string(),
  subtotal_price: z.string(),
  total_price: z.string(),
  batch_size: z.number().nullable(),
  effective_scaling_run_quantity: z.coerce.number().nullable(),
  batches_needed: z.number().nullable(),
  batch_effective_unit_cost: z.string().nullable(),
  pricing_model_id: z.number().nullable(),
  pricing_model_name: z.string().nullable(),
  material_id: z.number().nullable(),
  material_name: z.string().nullable(),
  material_sheet_sizes: MaterialSheetSizeItem.nullable(),
  markup_percentage: z.string().nullable(),
  tax_percentage: z.string().nullable(),
  cad_objects: z.array(CadObjectSummarySchema),
  inherited_cad_objects: z.array(CadObjectSummarySchema),
  dfm_checks: z.array(DFMCheckSchema),
  drawing_files: z.array(DrawingSummary),
  inherited_drawing_files: z.array(DrawingSummary),
  equipment_links: z.array(QuoteItemEquipment),
  consumable_links: z.array(ConsumableLink),
  cad_inherited_from_name: z.string().nullable(),
  cad_inherited_from_persistent_id: z.string().nullable(),
  cad_inherited_from_override_persistent_id: z.string().nullable(),
  parent_chain: z.array(
    z.object({
      persistent_id: z.string(),
      name: z.string(),
    }),
  ),
  pricing_information: z.array(PricingInformationEntry).nullable(),
  pricing_parameters: PricingParameters.nullable(),
  unit_cost_override: z.string().nullable(),
  quantity_override: z.string().nullable(),
  override_note: z.string(),
  calculated_unit_cost: z.string().nullable(),
  calculated_quantity: z.string().nullable(),
  inherit_material: z.boolean(),
  display_children_in_pdf: z.boolean(),
  get children() {
    return z.array(QuoteRevisionItem).nullable();
  },
});

export type QuoteRevisionItem = z.infer<typeof QuoteRevisionItem>;

export const QuoteRevisionItemsResponse = z.array(QuoteRevisionItem);

export const QuoteRevisionItemsMutationResponse = z.object({
  revision_number: z.number(),
  items: QuoteRevisionItemsResponse,
});
export type QuoteRevisionItemsMutationResponse = z.infer<
  typeof QuoteRevisionItemsMutationResponse
>;

/**
 * The bulk API deliberately accepts only direct, editable state. Prices,
 * inheritance output, DFM results, and catalog summaries are recalculated by
 * the server and returned in the hydrated `QuoteRevisionItem` tree.
 */
export type QuoteRevisionItemWrite = {
  persistent_id?: string;
  parent_persistent_id?: string | null;
  sibling_order?: number;
  name?: string;
  quantity?: string | number;
  quantity_type?: QuantityType;
  quantity_mode?: QuantityMode;
  unit_cost?: string | number;
  unit_markup?: string | number;
  batch_size?: number | null;
  tax_percentage?: string | number | null;
  markup_percentage?: string | number | null;
  pricing_model_id?: number | null;
  material_id?: number | null;
  inherit_material?: boolean;
  display_children_in_pdf?: boolean;
  cad_inherited_from_override_persistent_id?: string | null;
  pricing_parameters?: PricingParameters | null;
  unit_cost_override?: string | number | null;
  quantity_override?: string | number | null;
  override_note?: string;
  cad_object_ids?: number[];
  drawing_file_ids?: number[];
  equipment_links?: Array<{
    equipment_id: number;
    shifts?: number;
    hours_per_shift?: string | number;
    days_per_year?: number;
    equipment_efficiency?: string | number;
  }>;
  consumable_links?: ConsumableSelection[];
};
